import { useEffect, useRef, useState } from 'react'
import useWallet from './useWallet'
import { TOP_UP_CANISTER_ID, TopUp } from '../clients/TopUp'
import { CanisterInfo, TopUpType, UserProfile } from '../idls/topUp/topUp'
import { logger } from '../logger'
import { IcrcLedgerCanister } from '@dfinity/ledger-icrc'
import { getIdentity } from '../handlers/walletActions/wallet'
import getAgent from '../utils/getAgent'
import useBalances from './useBalances'
import { Principal } from '@dfinity/principal'
import { TCYCLES_CANISTER_ID } from '../constants/canisterIds'
import { decimalsToE8s } from '../utils/validationHelper'

export interface CanisterUpdateDetails {
  canisterId: string
  name?: string
  autoTopup?: boolean
  topupAmount?: string
  topupThreshold?: string
  snsRoot?: string
  topupMode: string
}

const useTopup = () => {
  const { wallet } = useWallet()
  const topUp = useRef<TopUp>()

  const [profile, setProfile] = useState<UserProfile>()
  const [editCanisterVisible, setEditCanisterVisible] = useState(false)
  const [loading, setLoading] = useState(true)

  const [canisterInDelete, setCanisterInDelete] = useState<CanisterInfo>()
  const [approvedBalance, setApprovedBalance] = useState(0)
  const [deleteInProgress, setDeleteInProgress] = useState(false)

  const { findBalance } = useBalances()
  const walletCyclesBalance = findBalance({ address: TCYCLES_CANISTER_ID })

  const loadProfile = async () => {
    if (topUp.current && wallet?.principalId) {
      setLoading(true)
      const profile = await topUp.current.getProfile(Principal.fromText(wallet?.principalId))

      setProfile(profile[0])
      setLoading(false)
    }
  }

  useEffect(() => {
    if (wallet?.principalId) {
      getIdentity(wallet?.principalId).then(async (identity) => {
        const agent = getAgent({ identity })
        topUp.current = new TopUp(agent)
        await loadProfile()
      })
    }
  }, [wallet?.principalId])

  useEffect(() => {
    refreshApproveBalance()
  }, [])

  const refreshApproveBalance = () => {
    const tcyclesLedger = IcrcLedgerCanister.create({
      agent: getAgent(),
      canisterId: Principal.from(TCYCLES_CANISTER_ID),
    })

    tcyclesLedger
      .allowance({
        account: {
          owner: Principal.from(wallet.principalId),
          subaccount: [],
        },
        spender: {
          owner: Principal.from(TOP_UP_CANISTER_ID),
          subaccount: [],
        },
      })
      .then((allowance) => setApprovedBalance(Number(allowance.allowance)))
      .catch(logger.debug)
  }

  const onCanisterDelete = () => {
    if (!canisterInDelete) return
    setDeleteInProgress(true)
    topUp.current
      ?.deleteCanister(canisterInDelete?.id)
      .then(async () => {
        setCanisterInDelete(undefined)
        await loadProfile()
      })
      .catch(logger.debug)
      .finally(() => setDeleteInProgress(false))
  }

  const convertToCanisterInfo = ({
    canisterId,
    autoTopup,
    name,
    topupAmount,
    topupThreshold,
    topupMode,
    snsRoot,
  }: CanisterUpdateDetails): CanisterInfo => {
    return {
      id: Principal.from(canisterId),
      is_healthy: true,
      auto_topup: autoTopup || false,
      name: name || '',
      topup_amount: BigInt(decimalsToE8s(topupAmount || '0', 12)),
      cycles_left: BigInt('0'),
      min_cycles: BigInt(decimalsToE8s(topupThreshold || '0', 12)),
      topup_type: { [topupMode]: null } as TopUpType,
      sns_root: snsRoot ? [Principal.from(snsRoot)] : [],
    }
  }

  const addCanister = async (canisterInfo: CanisterUpdateDetails) => {
    if (!topUp.current) return false

    await topUp.current.addCanister(convertToCanisterInfo(canisterInfo))
    loadProfile().catch(logger.debug)

    return true
  }

  const updateCanister = async (canisterInfo: CanisterUpdateDetails) => {
    if (!topUp.current) return false

    await topUp.current.updateCanister(convertToCanisterInfo(canisterInfo))
    loadProfile().catch(logger.debug)

    return true
  }

  return {
    profile,
    approvedBalance,
    walletCyclesBalance,
    wallet,
    loading,
    canisterInDelete,
    setCanisterInDelete,
    editCanisterVisible,
    setEditCanisterVisible,
    topUp,
    onCanisterDelete,
    deleteInProgress,
    refreshApproveBalance,
    addCanister,
    updateCanister,
    savedCanisters: profile?.canisters || [],
  }
}

export default useTopup
